/**
 * The shape every Volunteers screen has on `Volunteers.dc.html` (export 21), built once so ten
 * screens are the same drawing with different data: the header (eyebrow · year pills · title ·
 * charter · actions), a strip of figures, the builder's grid with the check-in curve beside it,
 * and cards — a colored top edge, a head and a sub, rows of a · b · c · chip · action, a foot.
 *
 * Nothing here computes a figure. Every number on these screens comes from the API, which reads
 * one engine (rule 2); a screen that has none says so in the drawing's own words.
 */
import type { ReactNode } from 'react';
import { dayOf, formatCountDay } from '@cider/ui';
import './volunteers.css';

export type Tone = 'ok' | 'warn' | 'bad' | 'info' | 'mute';
export type Edge = 'navy' | 'red' | 'gold' | 'sky' | 'green' | 'gray';

export interface Tile {
  n: string;
  label: string;
  sub?: string | undefined;
  tone?: 'green' | 'red' | 'gold' | undefined;
}
export interface Row {
  a: string;
  b?: string | null | undefined;
  c?: string | null | undefined;
  chip?: string | null | undefined;
  tone?: Tone | undefined;
  /** The action on the row, and what it does. A row with nothing behind it has no button. */
  act?: { label: string; onClick: () => void } | null | undefined;
  /** A second action, where the row has two answers (approve · decline). */
  act2?: { label: string; onClick: () => void } | null | undefined;
  /** A third, where the drawing gives three (This is … · New household · Not ours). */
  act3?: { label: string; onClick: () => void } | null | undefined;
  /** The wash on the row itself: the faintest tint there is. */
  wash?: 'bad' | 'warn' | 'ok' | undefined;
  key?: string | undefined;
}
export interface Card {
  head: string;
  sub?: string | undefined;
  rows: Row[];
  edge?: Edge | undefined;
  foot?: string | undefined;
  /** What the card says when it holds nothing — never an empty box. */
  empty?: string | undefined;
}

/** `Sunday` for an instant: the festival's own day, in words, as the drawing writes it. */
export function weekday(at: string): string {
  const w = formatCountDay(dayOf(at)).split(',')[0] ?? '';
  return w.charAt(0) + w.slice(1).toLowerCase();
}

export function Strip({ tiles }: { tiles: Tile[] }): ReactNode {
  if (tiles.length === 0) return null;
  return (
    <div className="vol__strip">
      {tiles.map((t) => (
        <div className="vol__tile" key={t.label}>
          <div className={`vol__tileN${t.tone ? ` vol__tileN--${t.tone}` : ''}`}>{t.n}</div>
          <div className="vol__tileLabel">{t.label}</div>
          {t.sub ? <div className="vol__tileSub">{t.sub}</div> : null}
        </div>
      ))}
    </div>
  );
}

export function Cards({ cards }: { cards: Card[] }): ReactNode {
  return (
    <div className="vol__cards">
      {cards.map((c) => (
        <section className={`vol__card vol__card--${c.edge ?? 'navy'}`} key={c.head}>
          <div className="vol__cardHead">
            <span className="vol__cardName">{c.head}</span>
            {c.sub ? <span className="vol__cardSub">{c.sub}</span> : null}
          </div>
          {c.rows.length === 0 ? (
            <div className="vol__row">
              <span className="vol__rowA">
                <b>{c.empty ?? 'Nothing here yet.'}</b>
              </span>
            </div>
          ) : (
            c.rows.map((r, i) => (
              <div
                className={`vol__row${r.wash ? ` vol__row--${r.wash}` : ''}`}
                key={r.key ?? `${r.a}-${String(i)}`}
              >
                <span className="vol__rowA">
                  <b>{r.a}</b>
                  {r.b ? <span className="vol__rowB">{r.b}</span> : null}
                </span>
                {r.c ? <span className="vol__rowC">{r.c}</span> : null}
                {r.chip ? (
                  <span className={`vol__chip vol__chip--${r.tone ?? 'mute'}`}>{r.chip}</span>
                ) : null}
                {r.act ? (
                  <button type="button" className="vol__rowAct" onClick={r.act.onClick}>
                    {r.act.label}
                  </button>
                ) : null}
                {r.act2 ? (
                  <button type="button" className="vol__rowAct" onClick={r.act2.onClick}>
                    {r.act2.label}
                  </button>
                ) : null}
                {r.act3 ? (
                  <button type="button" className="vol__rowAct" onClick={r.act3.onClick}>
                    {r.act3.label}
                  </button>
                ) : null}
              </div>
            ))
          )}
          {c.foot ? <div className="vol__cardFoot">{c.foot}</div> : null}
        </section>
      ))}
    </div>
  );
}

/** A volunteer's own screen at phone width, labeled as the drawing labels it. */
export function Phone({ cards }: { cards: Card[] }): ReactNode {
  return (
    <div className="vol__phone">
      <div className="vol__phoneLabel">AS A VOLUNTEER READS IT · PHONE WIDTH</div>
      <Cards cards={cards} />
    </div>
  );
}

export interface Action {
  label: string;
  onClick: () => void;
  primary?: boolean | undefined;
}

export function Screen({
  eyebrow,
  title,
  charter,
  years,
  year,
  onYear,
  actions,
  refusal,
  said,
  children,
}: {
  eyebrow: string;
  title: string;
  charter: string;
  years?: number[] | undefined;
  year?: number | undefined;
  onYear?: ((y: number) => void) | undefined;
  actions?: Action[] | undefined;
  refusal?: string | null | undefined;
  said?: string | null | undefined;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="vol">
      <div className="vol__head">
        <div className="vol__eyebrowRow">
          <span className="vol__eyebrow">{eyebrow}</span>
          {years?.length ? (
            <span className="vol__years">
              {years.map((y) => (
                <button
                  type="button"
                  key={y}
                  className="vol__year"
                  aria-pressed={y === year}
                  onClick={() => onYear?.(y)}
                >
                  {y}
                </button>
              ))}
            </span>
          ) : null}
        </div>
        <div className="vol__titleRow">
          <div className="vol__titleWrap">
            <h1 className="vol__title">{title}</h1>
            <p className="vol__charter">{charter}</p>
          </div>
          {actions?.length ? (
            <div className="vol__actions">
              {actions.map((a) => (
                <button
                  type="button"
                  key={a.label}
                  className={`vol__act${a.primary ? ' vol__act--primary' : ''}`}
                  onClick={a.onClick}
                >
                  {a.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <div className="vol__body">
        {refusal ? (
          <p className="ui-form__refusal" role="alert">
            {refusal}
          </p>
        ) : null}
        {said ? <p role="status">{said}</p> : null}
        <div className="vol__stack">{children}</div>
      </div>
    </div>
  );
}

/**
 * **Where it stands** (Commitment.dc.html): the navy line of figures across the top — the label in
 * gold, each figure with its words; a figure that needs eyes in gold too.
 */
export function NowStrip({
  label,
  figures,
}: {
  label: string;
  figures: { n: string; words: string; gold?: boolean }[];
}): ReactNode {
  return (
    <div className="vol__now" role="group" aria-label={label}>
      <span className="vol__nowLabel">{label}</span>
      {figures.map((f) => (
        <span className="vol__nowFig" key={f.words}>
          <b className={f.gold ? 'vol__nowN vol__nowN--gold' : 'vol__nowN'}>{f.n}</b>
          <span className="vol__nowWords">{f.words}</span>
        </span>
      ))}
    </div>
  );
}

/** One bar of a drawn tally: how many of the whole, in its color. */
export interface Bar {
  label: string;
  of: number;
  n: number;
  tone: 'green' | 'red' | 'gray';
}

/**
 * **A drawn tally** (Commitment.dc.html, "THE 212"): one bar a state, its share of the whole
 * filled, the count in words beside it.
 */
export function Bars({ head, sub, bars }: { head: string; sub: string; bars: Bar[] }): ReactNode {
  return (
    <section className="vol__bars" aria-label={head}>
      <div className="vol__barsHead">
        <span className="vol__barsTitle">{head}</span>
        <span className="vol__barsSub">{sub}</span>
      </div>
      {bars.map((b) => (
        <div className="vol__bar" key={b.label}>
          <span>{b.label}</span>
          <progress className={`vol__barTrack vol__barTrack--${b.tone}`} value={b.n} max={b.of}>
            {b.n}
          </progress>
          <span className="vol__barN">{`${String(b.n)} ${b.n === 1 ? 'family' : 'families'}`}</span>
        </div>
      ))}
    </section>
  );
}

/** The builder's grid: areas down, days across, each cell seats · signed up · came. */
export function Grid({
  head,
  rows,
  foot,
  onCell,
}: {
  head: string[];
  rows: { area: string; cells: ({ seats: number; signed: number; came: number } | null)[] }[];
  foot: string;
  onCell?: ((area: string, i: number) => void) | undefined;
}): ReactNode {
  return (
    <div className="vol__gridCard">
      <div className={`vol__grid vol__grid--c${String(Math.min(12, Math.max(2, head.length)))}`}>
        {head.map((h) => (
          <div className="vol__gridHead" key={h}>
            {h}
          </div>
        ))}
        {rows.map((r) => (
          <GridRow key={r.area} row={r} onCell={onCell} />
        ))}
      </div>
      <div className="vol__gridFoot">{foot}</div>
    </div>
  );
}

function GridRow({
  row,
  onCell,
}: {
  row: { area: string; cells: ({ seats: number; signed: number; came: number } | null)[] };
  onCell?: ((area: string, i: number) => void) | undefined;
}): ReactNode {
  return (
    <>
      <div className="vol__cell vol__cell--area">
        <b className="vol__cellArea">{row.area}</b>
      </div>
      {row.cells.map((c, i) => {
        if (!c)
          return <div className="vol__cell" key={`${row.area}-${String(i)}`} aria-hidden="true" />;
        const short = c.signed < c.seats * 0.85;
        return (
          <div
            className={`vol__cell${short ? ' vol__cell--short' : ''}`}
            key={`${row.area}-${String(i)}`}
            onClick={() => onCell?.(row.area, i)}
          >
            <span className="vol__cellV">{`${String(c.seats)} · ${String(c.signed)} · ${String(c.came)}`}</span>
            <span className={`vol__cellW${short ? ' vol__cellW--short' : ''}`}>
              {short ? `short ${String(c.seats - c.signed)}` : 'full'}
            </span>
          </div>
        );
      })}
    </>
  );
}

/** Check-ins by hour, from the season's own curve — never a number worked out on the page. */
export function Curve({
  head,
  sub,
  bars,
}: {
  head: string;
  sub: string;
  bars: { t: string; n: number }[];
}): ReactNode {
  const top = Math.max(1, ...bars.map((b) => b.n));
  return (
    <div className="vol__curve">
      <div className="vol__curveHead">{head}</div>
      <div className="vol__curveSub">{sub}</div>
      {bars.map((b) => (
        <div className="vol__bar" key={b.t}>
          <span className="vol__barT">{b.t}</span>
          <progress
            className={`vol__barTrack${b.n >= top / 2 ? ' vol__barTrack--busy' : b.n <= 1 ? ' vol__barTrack--quiet' : ''}`}
            value={b.n}
            max={top}
          >
            {b.n}
          </progress>
          <span className="vol__barN">{b.n}</span>
        </div>
      ))}
    </div>
  );
}
